import { Button } from '@tremor/react';
import clsx from 'clsx';
import { UseFormReturn } from 'react-hook-form';
import {
  getRegistrationPasswordRequirementStates,
  type RegisterFormValues,
} from '../firebaseAuthSchemas';
import { authTremorPrimarySubmitClass } from '../authFormTremor';
import { AuthTextField } from './AuthTextField';
import { AuthPasswordField } from './AuthPasswordField';

interface AuthRegisterFormProps {
  readonly form: UseFormReturn<RegisterFormValues>;
  readonly formDisabled: boolean;
  readonly showRegisterPassword: boolean;
  readonly showConfirmPassword: boolean;
  readonly onToggleRegisterPassword: () => void;
  readonly onToggleConfirmPassword: () => void;
  readonly onSubmit: (data: RegisterFormValues) => Promise<void>;
}

export function AuthRegisterForm({
  form,
  formDisabled,
  showRegisterPassword,
  showConfirmPassword,
  onToggleRegisterPassword,
  onToggleConfirmPassword,
  onSubmit,
}: AuthRegisterFormProps) {
  const { register, formState, handleSubmit, watch } = form;
  const passwordValue = watch('password') ?? '';
  const passwordRequirementStates =
    getRegistrationPasswordRequirementStates(passwordValue);

  return (
    <form
      className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-x-3 md:gap-y-1.5"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <AuthTextField
        id="register-name"
        label="Your name"
        type="text"
        autoComplete="name"
        disabled={formDisabled}
        dense
        error={formState.errors.displayName?.message}
        registration={register('displayName')}
      />
      <AuthTextField
        id="register-email"
        label="Email"
        type="email"
        autoComplete="username"
        disabled={formDisabled}
        dense
        error={formState.errors.email?.message}
        registration={register('email')}
      />
      <AuthPasswordField
        id="register-password"
        label="Password"
        autoComplete="section-registration new-password"
        disabled={formDisabled}
        dense
        showPassword={showRegisterPassword}
        onToggleShow={onToggleRegisterPassword}
        error={formState.errors.password?.message}
        registration={register('password')}
        passwordRequirementStates={passwordRequirementStates}
      />
      <AuthPasswordField
        id="register-confirm"
        label="Confirm password"
        autoComplete="section-registration new-password"
        disabled={formDisabled}
        dense
        showPassword={showConfirmPassword}
        onToggleShow={onToggleConfirmPassword}
        error={formState.errors.confirmPassword?.message}
        registration={register('confirmPassword')}
      />
      <Button
        type="submit"
        size="sm"
        color="rose"
        disabled={formDisabled}
        loading={formDisabled}
        loadingText="Creating account…"
        className={clsx(
          authTremorPrimarySubmitClass,
          '!py-2',
          'col-span-1 md:col-span-2',
        )}
      >
        Create account
      </Button>
    </form>
  );
}
